import { AfterViewInit, ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ButtonComponent } from '../../../buttons/button/button.component';
import { TypeaheadInputComponent } from '../../../form/inputs/typeahead-input/typeahead-input.component';
import { TableFilter, TableFilterOption } from '../../models/table-filter';
import { ButtonType } from '../../../enum/button-type.enum';
import { debounceTime, Observable, of, switchMap, tap } from 'rxjs';
import { InteractivePillComponent } from '../../../pills/interactive-pill/interactive-pill.component';
import { InputSize } from '../../../enum/input-size.enum';

@Component({
  selector: 'lib-typeahead-filter',
  imports: [ButtonComponent, ReactiveFormsModule, TypeaheadInputComponent, InteractivePillComponent],
  templateUrl: './typeahead-filter.component.html',
  styleUrl: './typeahead-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TypeaheadFilterComponent implements AfterViewInit {
  tableFilter = input.required<TableFilter>();
  minCharactersToStart = input<number>(2);
  debounceTime = input<number>(200);
  loadingResults = signal<boolean>(false);
  currentResults = signal<TableFilterOption[] | null>(null);
  resultStrings = computed(() => {
    return this.currentResults()?.map(option => option.name) ?? null;
  });

  typeaheadFormControl = new FormControl<string | null>(null);

  protected readonly ButtonType = ButtonType;
  protected readonly InputSize = InputSize;

  private listenToFormControl = this.typeaheadFormControl.valueChanges
    .pipe(
      debounceTime(this.debounceTime()),
      tap(() => {
        this.loadingResults.set(true);
      }),
      switchMap(v => {
        if (v === null || v.length < this.minCharactersToStart()) {
          // Hide results if the user has not entered enough characters
          return of(null);
        }
        if (this.tableFilter().fetchResultsFn && v !== null) {
          const fetchFn = this.tableFilter().fetchResultsFn as (v: string) => Observable<TableFilterOption[]>;
          return fetchFn(v);
        } else {
          return of(null);
        }
      }),
    )
    .subscribe(value => {
      this.loadingResults.set(false);
      this.currentResults.set(value);
    });

  resultIndexSelected(index: number) {
    const result = this.currentResults()?.[index];
    if (result) {
      if (!result.isSelected) {
        result.isSelected = signal(true);
      } else {
        result.isSelected?.set(true);
      }
      const selectedExists = this.tableFilter().unsavedOptions?.some(o => {
        return o.value === result.value;
      });
      if (!selectedExists) {
        this.tableFilter().unsavedOptions?.push(result);
      }
    }
  }

  pillOptionsClicked(filterOption: TableFilterOption) {
    this.tableFilter().unsavedOptions = this.tableFilter().unsavedOptions?.filter(o => o.value !== filterOption.value);
  }

  ngAfterViewInit(): void {
    this.tableFilter()?.resetAll$.subscribe(() => {
      // the typeahead doesn't actually need to be reset,
      // this line is really just used to poke the change detection on this component
      this.typeaheadFormControl.reset();
    });
  }

  resetClicked() {
    this.tableFilter().resetUnsavedOptions();
  }
}
